<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';

import { DataObjectRenameDocument, DataObjectType } from '@/gql/graphql';

import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import { formatFileName } from '@/utils/file-utils';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  dataObject: DataObjectType;
  bucketId: number;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const dataObjectNameUpdate = ref<string>(formatFileName(props.dataObject.name));

const show = computed<boolean>(() => {
  return props.show;
});

// --- Mutation: Rename Data Object ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(DataObjectRenameDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.dataObjectRename) {
    console.debug('Data object renamed:', res.data.dataObjectRename);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Renaming failed: ' + res.data;
    console.error('Data object renaming failed:', res.data);
  }
});

onMutationError((err) => {
  console.error('Error renaming data object:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onRenameConfirmed() {
  if (!props.dataObject) {
    mutationError.value = 'Data object not provided for deletion';
    console.error('Data object not provided for deletion');
    return;
  }
  if (typeof props.dataObject.name !== 'string') {
    mutationError.value = 'Data object name is not a string';
    console.error('Data object name is not a string:', props.dataObject.name);
    return;
  }
  if (typeof props.dataObject.isFolder !== 'boolean') {
    mutationError.value = 'Data object `isFolder` field type is not a boolean';
    console.error(
      'Data object `isFolder` field type is not a boolean:',
      props.dataObject.isFolder
    );
    return;
  }
  if (!dataObjectNameUpdate.value) {
    mutationError.value = 'New name is empty';
    console.error('New name is empty');
    return;
  }

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({
    bucketId: props.bucketId,
    oldObjectName: props.dataObject.name,
    newObjectName: dataObjectNameUpdate.value,
    isFolder: props.dataObject.isFolder,
  });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL: Storage Object Rename -->
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Title -->
      <template #title>
        <font-awesome-icon icon="pencil" class="me-2" />
        Rename
        <strong>{{ formatFileName(dataObject?.name) }}</strong>
        <template v-if="dataObject">
          <span v-if="dataObject.isFolder"> folder </span>
          <span v-else> file </span>
        </template>
      </template>
      <!-- Content -->
      <div v-if="dataObject">
        <!-- Rename Form Elements-->
        <BFormGroup
          label="New name"
          label-for="storage-object-name-update"
          class="mt-3"
        >
          <BFormInput
            v-model="dataObjectNameUpdate"
            id="storage-object-name-update"
            name="storage-object-name-update"
            required
          ></BFormInput>
        </BFormGroup>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>
      <!-- Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button variant="primary" @click="onRenameConfirmed">
          <span v-if="mutationInProgress">
            Saving Changes
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Changes Saved
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Save Changes </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
